/**
 * THE SUBPATH, NOT THE BARREL — and this is load-bearing, not tidiness.
 *
 * `@ecomally/contracts` re-exports `identity/identity-hash.ts`, which imports
 * `node:crypto`. Importing the barrel from browser code puts that in the bundle, and Vite
 * externalises it into a stub that throws the moment anything touches it:
 *
 *     Module "node:crypto" has been externalized for browser compatibility.
 *
 * The dashboard had NO contracts import before 2026-09-18; this was the first, and it
 * broke the page at runtime rather than at build. `packages/tracker` hit the identical
 * wall the same week and got `./tracker/url-params` for the same reason.
 */
import { UNMATCHED_REASONS_PATH, type UnmatchedReasonRow } from "@ecomally/contracts/reports/unmatched-reasons";
import type { AxiosInstance } from "axios";

import type { JourneyModel } from "./customer-journey.js";
import { formatCount, formatMoney, formatPercent, formatRatio, formatText, formatWeek } from "./format.js";

export const GOOGLE_CAMPAIGN_TYPES = [
    "PERFORMANCE_MAX",
    "SHOPPING",
    "DEMAND_GEN",
    "SEARCH",
    "VIDEO",
    "MULTI_CHANNEL",
    "UNATTRIBUTED",
    "OTHER",
] as const;
export type GoogleCampaignType = (typeof GOOGLE_CAMPAIGN_TYPES)[number];

export const GOOGLE_CAMPAIGN_TYPE_LABELS: Record<GoogleCampaignType, string> = {
    PERFORMANCE_MAX: "Performance Max",
    SHOPPING: "Shopping",
    DEMAND_GEN: "Demand Gen",
    SEARCH: "Search",
    VIDEO: "Video",
    MULTI_CHANNEL: "Multi-channel",
    UNATTRIBUTED: "Unattributed",
    OTHER: "Other / not synced",
};

export const GOOGLE_CAMPAIGN_HIERARCHIES = ["campaign", "adgroup", "ad", "assetgroup", "productgroup"] as const;
export type GoogleCampaignHierarchy = (typeof GOOGLE_CAMPAIGN_HIERARCHIES)[number];

export const GOOGLE_CAMPAIGN_HIERARCHY_LABELS: Record<GoogleCampaignHierarchy, string> = {
    campaign: "Campaign",
    adgroup: "Ad group",
    ad: "Ad",
    assetgroup: "Asset group",
    productgroup: "Product group",
};


/**
 * Mirrors ALLOWED_HIERARCHY_BY_CAMPAIGN_TYPE in the API's google-campaign-attribution
 * module. Duplicated on purpose rather than imported across the api/web boundary — same
 * reasoning as PAGE_SIZES/MAX_LIMIT above: offering a combination the API rejects is a
 * 400 the merchant sees as a broken page.
 */
export const ALLOWED_GOOGLE_HIERARCHY_BY_CAMPAIGN_TYPE: Record<GoogleCampaignType, readonly GoogleCampaignHierarchy[]> = {
    PERFORMANCE_MAX: ["campaign", "assetgroup"],
    SHOPPING: ["campaign", "adgroup", "productgroup"],
    DEMAND_GEN: ["campaign", "adgroup", "ad"],
    SEARCH: ["campaign", "adgroup", "ad"],
    VIDEO: ["campaign", "adgroup", "ad"],
    MULTI_CHANNEL: ["campaign", "assetgroup", "ad"],
    UNATTRIBUTED: ["campaign"],
    OTHER: ["campaign"],
};

/**
 * What clicking a row drills into, for Google's campaign report — restricted to the
 * campaign types where a click-through hierarchy is meaningful on this data.
 *
 *   - SHOPPING's next level is "productgroup" — a product catalog, not another slice of
 *     the same campaign table, so clicking through doesn't mean "zoom in" here.
 *   - MULTI_CHANNEL's "assetgroup" is ad-group grain under the hood (see
 *     googleHierarchyShape's MULTI_CHANNEL branch) and gets no click-to-drill step.
 *   - "All types", UNATTRIBUTED and OTHER have no lower level at all — the Level control
 *     is already locked for them (see `hierarchyLocked` in ReportScreen).
 *
 * A missing type entry or a `null` value (a terminal level, e.g. PMax's "assetgroup")
 * both mean "not clickable" — callers should treat them the same way.
 */
export const GOOGLE_HIERARCHY_CHILD: Partial<
    Record<GoogleCampaignType, Partial<Record<GoogleCampaignHierarchy, GoogleCampaignHierarchy | null>>>
> = {
    PERFORMANCE_MAX: {
        campaign: "assetgroup",
        assetgroup: null,
    },
    SEARCH: {
        campaign: "adgroup",
        adgroup: "ad",
        ad: null,
    },
    DEMAND_GEN: {
        campaign: "adgroup",
        adgroup: "ad",
        ad: null,
    },
    VIDEO: {
        campaign: "adgroup",
        adgroup: "ad",
        ad: null,
    },
    SHOPPING: {
        campaign: "adgroup",
        adgroup: "productgroup",
        productgroup: null,
    },
};

/** Hierarchy options with no campaign type selected — assetgroup/productgroup need one. */
export const DEFAULT_GOOGLE_HIERARCHIES: readonly GoogleCampaignHierarchy[] = ["campaign", "adgroup", "ad"];

/**
 * Meta's drill levels. Simpler than Google's: there is no campaignType concept and no
 * type-specific table, so every level is always offered — no lock, no "requires a type
 * first" case.
 */
export const META_CAMPAIGN_HIERARCHIES = ["campaign", "adset", "ad"] as const;
export type MetaCampaignHierarchy = (typeof META_CAMPAIGN_HIERARCHIES)[number];

export const META_CAMPAIGN_HIERARCHY_LABELS: Record<MetaCampaignHierarchy, string> = {
    campaign: "Campaign",
    adset: "Ad set",
    ad: "Ad",
};

/**
 * What the reconciliation line is called at each level.
 *
 * Written out rather than built from the labels above, because the article changes
 * ("a campaign", "an ad set") and a sentence assembled from parts gets that wrong.
 *
 * It has to change with the level because the SERVER's answer changes with the level:
 * inside a campaign the row means "in this campaign, but no ad set", not "unmatched
 * anywhere in the store". Labelling all three the same is how a Total of 15 appeared
 * under an ad set holding 5 orders.
 */
export const META_UNATTRIBUTED_LABELS: Record<MetaCampaignHierarchy, string> = {
    campaign: "Not matched to a campaign",
    adset: "Not matched to an ad set",
    ad: "Not matched to an ad",
};

/**
 * The same line on the GOOGLE report, which did not have one at all until 2026-09-16.
 * Orders whose Google click never resolved simply left the table, so the Total could
 * never match Source Attribution — 618 against 766 on Celestial for 10-16 Sep.
 *
 * Written out rather than derived from the hierarchy labels because the article changes
 * ("a campaign", "an ad group"), and a sentence assembled from parts gets that wrong.
 */
export const GOOGLE_UNATTRIBUTED_LABELS: Record<GoogleCampaignHierarchy, string> = {
    campaign: "Not matched to a campaign",
    adgroup: "Not matched to an ad group",
    ad: "Not matched to an ad",
    assetgroup: "Not matched to an asset group",
    productgroup: "Not matched to a product group",
};

/**
 * THE ONE PLACE THE REPORT CONTRACT IS WRITTEN DOWN — paths, sortable columns, limits.
 *
 * Legacy spread this across five screens of 600-1,500 lines each, every one repeating its
 * own axios call, its own date state and its own table markup. The consequence was not
 * ugliness, it was DRIFT: a column renamed in the API stayed correct on the screens
 * someone remembered to edit. Here the five reports differ only in this table, so a
 * screen cannot disagree with the API about a column it never names.
 *
 * THREE THINGS THE API REFUSES RATHER THAN FORGIVES, all encoded here:
 *
 *   1. `from`/`to` are CALENDAR DAYS in the store's timezone (`^\d{4}-\d{2}-\d{2}$`), not
 *      instants. An ISO timestamp is a 400, not a coerced date.
 *   2. `sortBy` is a closed enum PER REPORT, and they are not the same enum. Offering a
 *      column one report cannot sort by is a 400 the merchant sees as a broken page.
 *   3. `limit` is capped at 1,000 and the cap REFUSES rather than clamps, so no control
 *      here may offer a larger number.
 */

export type CellKind = "text" | "count" | "money" | "percent" | "ratio" | "week" | "status";

/**
 * How a column's TOTAL is produced, or that it has none.
 *
 * NOT EVERY COLUMN CAN BE ADDED UP, and quietly summing the ones that cannot is how a
 * footer row becomes a lie:
 *
 *   • `total_orders` counts orders that TOUCHED a source. One order touching meta and
 *     google is counted by both, so the column sums to more than the orders that exist —
 *     legacy's own screen shows a Platform column summing to 34 above a total of 24.
 *   • `visitors` overlaps the same way.
 *   • a ROAS is a RATIO. Adding ratios is meaningless; the total ROAS is total revenue
 *     over total spend, which is what `ratio` computes.
 *
 * A column with no rule shows an em dash in the footer rather than a number nobody can
 * defend.
 */
export type ColumnTotal =
    | { readonly kind: "sum" }
    /**
     * For a column where a per-row value is real but ADDING rows is not — a visitor
     * reachable from two sources is a row under each, so summing (by any means, over any
     * set of rows) counts them twice. The true total is a DISTINCT count the API computes
     * once over the whole window and sends back as its own figure, never derived from the
     * rows on screen. See `columnTotal`: this only ever trusts a SINGLE row, exactly the
     * shape the API's `totals` response takes once wrapped as `[totals]` — with more than
     * one row (no `totals` sent, real per-source/per-entity rows on screen instead) it
     * returns null rather than risk adding them.
     */
    | { readonly kind: "distinct" }
    | {
        readonly kind: "ratio";
        readonly numerator: string;
        readonly denominator: string;
        /**
         * Multiplier applied after dividing. 100 for a percentage, because the API's
         * per-row percentages are already scaled (`(orders / sessions) * 100` in the SQL)
         * and a total recomputed from raw sums would otherwise come back as 0.03 where
         * every row above it says 3.
         */
        readonly scale?: number;
        /**
         * Read the ratio off the API's totals row ALONE, never adding the unmatched line to it. For a ratio whose denominator is
         * a `distinct` column (NCAC = spend / new customers): the API already folded the line into that distinct figure, so adding the
         * line's own count on top would count its buyers twice.
         */
        readonly totalsOnly?: boolean;
    };

export interface ReportColumn {
    readonly key: string;
    readonly label: string;
    readonly kind: CellKind;
    /** The header band this column sits under. Absent = a plain, ungrouped column. */
    readonly group?: string;
    /**
     * Why this column shows an em dash instead of a total.
     *
     * A bare dash reads as "broken", and the honest reason is short enough to say. Shown as
     * the cell's tooltip so it costs no width in a table that is already eighteen columns
     * across.
     */
    readonly noTotalReason?: string;
    /**
     * Render this cell with the traffic source's brand mark. Only meaningful on the column
     * that HOLDS a source name — putting it on a campaign name would look up "Summer Sale
     * — Prospecting" as a platform and always miss.
     */
    readonly brandMark?: true;
    /**
     * Pin this column so it never scrolls out of view horizontally — legacy freezes the
     * name/status column plus the whole "Platform" group (Spend, ROAS, Purchases, Revenue)
     * on Source, Meta and Google Attribution, so a merchant scrolling into FC/LC/DDA/Linear/
     * Triple territory never loses sight of what they're spending and what the platform
     * itself claims. Order matters: columns are pinned left-to-right in the order they
     * appear in `columns`, each stacking after the ones before it.
     */
    readonly sticky?: true;
    /**
     * Pixel width for a sticky column. Meaningless on a non-sticky column — those stay
     * auto-width, exactly as before. A sticky column needs a FIXED width because several
     * of them stack via `left: <sum of the widths before it>`, and content wider than the
     * assumed width would drift the columns after it out of alignment. Unset falls back to
     * a width wide enough for a typical money/ratio cell.
     */
    readonly width?: number;
    /**
     * Shown in only ONE of the report's returns views (see `ReportDefinition.returnsView`): the
     * last-click figures before cancellations and refunds, or after them. Unset: shown in both.
     */
    readonly returnsView?: ReturnsView;
    readonly total?: ColumnTotal;
    /**
     * Makes this a Purchases cell that opens the Customer Journey & Orders modal, listing
     * the orders behind the number under this attribution model. Set only on the columns
     * whose figure is counted from OUR orders — never on the ad platform's own reported
     * Purchases, which has no orders behind it to list.
     */
    readonly journeyModel?: JourneyModel;
    /**
     * Present only when the API's `sortBy` enum for THIS report accepts this column.
     * Absent means the header is not clickable — which is honest, rather than a click that
     * produces a 400.
     */
    readonly sortKey?: string;
}

/**
 * Source Attribution's "objective" and "attribution model" pickers, ported from
 * `ecom_code`'s redesigned report (owner request, 2026-09-11).
 *
 * Spend/Platform and the "EA Matrix" traffic columns are always on screen; on top of
 * that baseline, exactly the MODEL GROUPS in `selectedModels` are shown. Picking an
 * objective PINS `selectedModels` to that objective's models and disables the model
 * picker — legacy's exact mutual-exclusion, ported rather than reinvented.
 */
export interface AttributionModelOption {
    readonly key: string;
    readonly label: string;
    /** Column `group` names this model turns on. */
    readonly groups: readonly string[];
}

export interface AttributionObjective {
    readonly key: string;
    readonly title: string;
    readonly description: string;
    /** `AttributionModelOption.key`s this objective pins `selectedModels` to. */
    readonly modelKeys: readonly string[];
}

export interface AttributionModelToggle {
    /** Column groups shown regardless of model selection — "Platform", "EA Matrix", "New customer" and "Assistance attribution". */
    readonly alwaysGroups: readonly string[];
    readonly models: readonly AttributionModelOption[];
    readonly objectives: readonly AttributionObjective[];
    /** `selectedModels` when no objective is active — legacy's default view (FC, LC, DDA). */
    readonly defaultModelKeys: readonly string[];
}

/** The columns visible for a given model selection — always-on groups plus the selected models'. */
/**
 * BEFORE OR AFTER RETURNS (owner, 2026-10-30). An agency shows a merchant what its ads SOLD — every
 * order at its own value, cancelled and refunded ones included — and, a click away, what the
 * merchant's cancellations and refunds left. "original" is the default.
 */
export type ReturnsView = "original" | "net";

export function visibleColumnsFor(
    report: ReportDefinition,
    selectedModelKeys: ReadonlySet<string>,
    returnsView: ReturnsView = "original",
): readonly ReportColumn[] {
    const columns = report.columns.filter(
        (column) => column.returnsView === undefined || report.returnsView !== true || column.returnsView === returnsView,
    );
    const toggle = report.attributionModelToggle;
    if (toggle === undefined) return columns;
    const activeGroups = new Set<string>(toggle.alwaysGroups);
    for (const model of toggle.models) {
        if (selectedModelKeys.has(model.key)) {
            for (const group of model.groups) activeGroups.add(group);
        }
    }
    return columns.filter((column) => column.group === undefined || activeGroups.has(column.group));
}

export interface ReportDefinition {
    readonly key: string;
    readonly label: string;
    /** The line under the title. Legacy shows one on every report page. */
    readonly subtitle: string;
    /**
     * The label for the reconciliation row, when this report has one.
     *
     * Each report is missing something DIFFERENT: a campaign table cannot show orders that
     * matched no campaign, a landing-page table cannot show orders that reached no landing
     * page. One generic word for both would explain neither.
     */
    readonly unattributedLabel?: string;
    /** Which key in the remainder counts the orders this report could not place. */
    readonly coverageKey?: string;
    /**
     * WHAT THIS REPORT COUNTS, in one sentence, shown under the table.
     *
     * Two reports over the same store and window legitimately disagree, and without this
     * the merchant reads that as one of them being broken. Measured 2026-09-03 on a
     * controlled dataset: Landing Page Analysis showed 1 order where Source Attribution
     * showed 2, because an order whose session was never tracked has no landing page to
     * attach to and is dropped — while Source Attribution keeps it under `[Blank]`.
     *
     * The difference is real and neither number is wrong. Saying so is the fix; making
     * them agree would mean throwing away a true row from one of them.
     */
    readonly scopeNote: string;
    /** Lowercase, exactly as spelled here: Fastify's router is case-sensitive; Express was not. */
    readonly path: string;
    readonly columns: readonly ReportColumn[];
    readonly defaultSort: string | null;
    /**
     * The column carrying a total row count, where the query computes one.
     *
     * Only Source Attribution does — `count(*) OVER () AS total_source_count`. The other
     * four return rows and nothing else, so their pager cannot honestly say "of 214". It is
     * NOT in `columns`: it is the same number on every row, and a merchant reading a table
     * of metrics has no use for a column that repeats itself.
     */
    readonly totalKey?: string;
    /** The DOW report takes no `sortBy` at all — it is ordered by the query itself. */
    readonly sortable: boolean;
    /**
     * Whether this report accepts the active/inactive filter. Only the campaign reports do
     * — a traffic SOURCE has no on/off switch, and sending `status` to a schema without
     * the field would silently do nothing (zod strips unknown keys), which is a control
     * that looks like it works.
     */
    readonly statusFilter?: true;
    /**
     * Whether this report accepts the Google-only campaignType/hierarchy drill controls.
     * Only the Google campaign report does — Meta has its OWN, simpler hierarchy (see
     * `metaHierarchy` below), so the two are never both true on the same report.
     */
    readonly googleHierarchy?: true;
    /**
     * Whether this report accepts Meta's hierarchy drill control (campaign/adset/ad).
     * Simpler than Google's: no campaignType, no type-specific tables, so every level is
     * always offered — the Level dropdown is never locked the way Google's can be.
     */
    readonly metaHierarchy?: true;
    /** Source Attribution, Meta and Google Campaign Attribution have this — see `AttributionModelToggle`. */
    readonly attributionModelToggle?: AttributionModelToggle;
    /**
     * The Day / Week view of this report (the three attribution reports): where the per-period figures of the rows on screen
     * come from, which row field identifies an entity to that endpoint (`ids`), and which one names it.
     */
    readonly periodBreakdown?: {
        readonly path: string;
        readonly entityKey: string;
        readonly labelKey: string;
    };
    /**
     * The report offers "Before returns" / "After returns" (see `ReturnsView`): its columns tagged
     * with a `returnsView` show in that view only, and the totals carry the bridge between the two.
     */
    readonly returnsView?: true;
}

/**
 * Which campaigns a campaign report is showing. Mirrors the API's `status` enum.
 *
 * `all` is the DEFAULT and stays the default: a paused campaign still spent money and
 * still earned the orders it earned while it ran, so hiding it by default would make the
 * page disagree with Source Attribution for a reason nobody could see.
 */
export const CAMPAIGN_STATUSES = ["all", "active", "inactive"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const MAX_LIMIT = 1000;
export const PAGE_SIZES = [25, 50, 100, 250, 500, 1000] as const;

/**
 * Strips the sort affordance from a shared column set.
 *
 * A column that carries `sortKey` renders a CLICKABLE header. The DOW report's schema has
 * no `sortBy` at all, so those clicks would set local state, flip an arrow, and change
 * nothing about the data — the "control that appears to work" this file warns about
 * above. Reusing the columns is right; reusing their sortability is not.
 */
function withoutSort(columns: readonly ReportColumn[]): readonly ReportColumn[] {
    // Rebuilding the object from three named fields silently dropped `total`,
    // `noTotalReason` and `group` as well — so the DOW report lost every footer figure it
    // was entitled to. Copy the column and remove ONLY the sort affordance.
    return columns.map((column) => {
        const { sortKey, ...rest } = column;
        void sortKey;
        return rest;
    });
}

/** Shared by the two landing-page reports — the DOW one is this plus a week column. */
export const LANDING_PAGE_COLUMNS: readonly ReportColumn[] = [
    { key: "url", sortKey: "url", label: "Landing page", kind: "text", width: 280 },
    { key: "session_count", label: "Sessions", kind: "count", sortKey: "session_count", total: { kind: "distinct" }, width: 110 },
    { key: "page_view_count", label: "Page views", kind: "count", sortKey: "page_view_count", total: { kind: "sum" }, width: 120 },
    { key: "visitor_count", label: "Visitors", kind: "count", sortKey: "visitor_count", total: { kind: "distinct" }, width: 110 },
    { key: "new_visitor_count", sortKey: "new_visitor_count", label: "New visitors", kind: "count", total: { kind: "distinct" }, width: 135 },
    { key: "new_visitor_percentage", sortKey: "new_visitor_percentage", label: "New visitor %", kind: "percent", total: { kind: "ratio", numerator: "new_visitor_count", denominator: "visitor_count", scale: 100 }, width: 145 },
    { key: "session_per_visitor", sortKey: "session_per_visitor", label: "Session per visitor", kind: "ratio", total: { kind: "ratio", numerator: "session_count", denominator: "visitor_count" }, width: 175 },
    { key: "order_count", label: "Orders", kind: "count", sortKey: "order_count", total: { kind: "sum" }, width: 110 },
    { key: "order_value", label: "Order value", kind: "money", sortKey: "order_value", total: { kind: "sum" }, width: 130 },
    { key: "cvr_percentage", label: "CVR", kind: "percent", sortKey: "cvr_percentage", total: { kind: "ratio", numerator: "order_count", denominator: "session_count", scale: 100 }, width: 100 },
    // DISTINCT, not sum: traffic is counted per URL SEEN, so a non-ecom session that saw three pages sits in three rows.
    // The footer is the API's store-wide uniqExact count (landingStoreTotals), never the rows added up.
    { key: "non_ecom_traffic", sortKey: "non_ecom_traffic", label: "Non-ecom traffic", kind: "count", total: { kind: "distinct" }, width: 165 },
    { key: "non_ecom_traffic_percentage", sortKey: "non_ecom_traffic_percentage", label: "Non-ecom traffic %", kind: "percent", total: { kind: "ratio", numerator: "non_ecom_traffic", denominator: "session_count", scale: 100 }, width: 180 },
    { key: "non_ecom_visitor_count", sortKey: "non_ecom_visitor_count", label: "Non-ecom visitors", kind: "count", total: { kind: "distinct" }, width: 175 },
    { key: "non_ecom_visitor_percentage", sortKey: "non_ecom_visitor_percentage", label: "Non-ecom visitor %", kind: "percent", total: { kind: "ratio", numerator: "non_ecom_visitor_count", denominator: "visitor_count", scale: 100 }, width: 185 },
    { key: "customer_count", sortKey: "customer_count", label: "Customers", kind: "count", total: { kind: "distinct" }, width: 125 },
    { key: "customer_percentage", sortKey: "customer_percentage", label: "Customer %", kind: "percent", total: { kind: "ratio", numerator: "customer_count", denominator: "visitor_count", scale: 100 }, width: 135 },
    { key: "new_customer_count", sortKey: "new_customer_count", label: "New customer", kind: "count", total: { kind: "distinct" }, width: 150 },
    { key: "new_customer_percentage", sortKey: "new_customer_percentage", label: "New customer %", kind: "percent", total: { kind: "ratio", numerator: "new_customer_count", denominator: "customer_count", scale: 100 }, width: 165 },
];

const CAMPAIGN_COLUMNS: readonly ReportColumn[] = [
    // NAME/STATUS/PLATFORM ARE FROZEN (owner request, 2026-09-11, ported from
    // `ecom_code`'s redesign — the same freeze Source Attribution's Platform group gets
    // below). Scrolling right into FC/LC/DDA/Linear/Triple territory must never lose sight
    // of which campaign this is, whether it is running, or what it costs and claims.
    { key: "campaign_name", sortKey: "campaign_name", label: "Campaign", kind: "text", sticky: true, width: 200 },
    // Beside the name rather than out at the end: it says what the campaign IS, not how it
    // performed, and a merchant reading a row of poor numbers needs to know it is paused
    // before drawing any conclusion from them.
    { key: "status", sortKey: "status", label: "Status", kind: "status", noTotalReason: "No total: a status is a label, not a quantity.", sticky: true, width: 90 },

    { key: "spend", label: "Spend", kind: "money", sortKey: "spend", group: "Platform", total: { kind: "sum" }, sticky: true, width: 110 },
    { key: "impressions", sortKey: "impressions", label: "Impressions", kind: "count", group: "Platform", total: { kind: "sum" }, sticky: true, width: 140 },
    { key: "clicks", sortKey: "clicks", label: "Clicks", kind: "count", group: "Platform", total: { kind: "sum" }, sticky: true, width: 100 },
    { key: "platform_revenue", label: "Revenue", kind: "money", sortKey: "platform_revenue", group: "Platform", total: { kind: "sum" }, sticky: true, width: 120 },
    { key: "platform_roas", label: "ROAS", kind: "ratio", sortKey: "platform_roas", group: "Platform", total: { kind: "ratio", numerator: "platform_revenue", denominator: "spend" }, sticky: true, width: 100 },
    // The platform's OWN reported conversions — not derived from our order matching,
    // unlike every other Purchases column here. Same definition as source-attribution.ts's
    // platform_orders.
    { key: "platform_orders", sortKey: "platform_orders", label: "Purchases", kind: "count", group: "Platform", total: { kind: "sum" }, sticky: true, width: 110 },

    { key: "fc_orders", journeyModel: "fc", sortKey: "fc_orders", label: "Purchases", kind: "count", group: "First click", total: { kind: "sum" } },
    { key: "fc_revenue", sortKey: "fc_revenue", label: "Revenue", kind: "money", group: "First click", total: { kind: "sum" } },
    // Source Attribution has carried a first-click ROAS since it was built; the campaign
    // tables showed first-click revenue with no return beside it, so the one question the
    // page exists to answer — is this campaign worth what it costs — could be asked of the
    // closing touch and not the opening one.
    { key: "fc_roas", sortKey: "fc_roas", label: "ROAS", kind: "ratio", group: "First click", total: { kind: "ratio", numerator: "fc_revenue", denominator: "spend" } },
    { key: "fc_new_customer_percentage", sortKey: "fc_new_customer_percentage", label: "New customer %", kind: "percent", group: "First click", noTotalReason: "No total: both sides overlap across entities.", width: 140 },

    { key: "lc_orders", journeyModel: "lc", label: "Purchases", kind: "count", sortKey: "lc_orders", group: "Last click", total: { kind: "sum" } },
    { key: "lc_revenue", label: "Revenue", kind: "money", sortKey: "lc_revenue", group: "Last click", total: { kind: "sum" } },
    { key: "lc_roas", sortKey: "lc_roas", label: "ROAS", kind: "ratio", group: "Last click", total: { kind: "ratio", numerator: "lc_revenue", denominator: "spend" } },

    // FRACTIONAL — each entity's SHARE of the orders it helped win, same reasoning as
    // source-attribution.ts's dda_orders. Newly added alongside dda_revenue/dda_roas,
    // which this report already carried.
    { key: "dda_orders", journeyModel: "dda", sortKey: "dda_orders", label: "Purchases", kind: "count", group: "Data-driven", total: { kind: "sum" } },
    { key: "dda_revenue", sortKey: "dda_revenue", label: "Revenue", kind: "money", group: "Data-driven", total: { kind: "sum" } },
    { key: "dda_roas", sortKey: "dda_roas", label: "ROAS", kind: "ratio", group: "Data-driven", total: { kind: "ratio", numerator: "dda_revenue", denominator: "spend" } },

    // Linear — the "unbiased baseline": every touch on an order gets an EQUAL share,
    // whichever entity it belongs to, N being the WHOLE journey's touch count. Fractional
    // for the same reason DDA's is.
    { key: "linear_orders", journeyModel: "linear", sortKey: "linear_orders", label: "Purchases", kind: "count", group: "Linear", total: { kind: "sum" } },
    { key: "linear_revenue", sortKey: "linear_revenue", label: "Revenue", kind: "money", group: "Linear", total: { kind: "sum" } },
    { key: "linear_roas", sortKey: "linear_roas", label: "ROAS", kind: "ratio", group: "Linear", total: { kind: "ratio", numerator: "linear_revenue", denominator: "spend" } },

    // Triple — every entity an order touched gets the WHOLE order, full revenue, no
    // sharing. NO TOTAL: an order touching two entities is counted, in full, under both.
    { key: "triple_orders", journeyModel: "triple", sortKey: "triple_orders", label: "Purchases", kind: "count", group: "Triple", noTotalReason: "No total: an order touching two entities is counted in full under each, so adding the column would count it twice." },
    { key: "triple_revenue", sortKey: "triple_revenue", label: "Revenue", kind: "money", group: "Triple", noTotalReason: "No total: an order touching two entities is counted in full under each, so adding the column would count its revenue twice." },
    { key: "triple_roas", sortKey: "triple_roas", label: "ROAS", kind: "ratio", group: "Triple", noTotalReason: "No total: the revenue above overlaps between entities, so there is no honest ratio to sum." },

    // ASSISTANCE ATTRIBUTION (ecom_code's assisted attribution): orders this entity ASSISTED — it had a touch in the order's journey that was
    // not the last click — and those orders' revenue. 20 of Google's 100 last-click orders had a Meta touch earlier -> Meta has 20 assisted orders.
    // NO TOTAL: an order assisted by two entities is counted under each, so adding the column would count it (and its revenue) twice.
    { key: "assisted_orders", sortKey: "assisted_orders", label: "Orders", kind: "count", group: "Assistance attribution", noTotalReason: "No total: an order assisted by two entities is counted under each, so adding the column would count it twice.", width: 120 },
    { key: "assisted_revenue", sortKey: "assisted_revenue", label: "Revenue", kind: "money", group: "Assistance attribution", noTotalReason: "No total: an order assisted by two entities is counted under each, so adding the column would count its revenue twice.", width: 130 },

    // NEW CUSTOMER — first-click buyers who are new to the store in the window (first-ever visit inside it), and what each cost.
    // NOT SUMMED: a buyer who is new under two campaigns is one customer in the Total, so `distinct` reads the API's own count.
    { key: "new_customers", sortKey: "new_customers", label: "New customers", kind: "count", group: "New customer", total: { kind: "distinct" }, width: 140 },
    // NCAC = platform spend / new customers. The Total is total spend over the DISTINCT new customers.
    { key: "ncac", sortKey: "ncac", label: "NCAC", kind: "money", group: "New customer", total: { kind: "ratio", numerator: "spend", denominator: "new_customers", totalsOnly: true }, width: 120 },

    // EA Matrix — our OWN tracking, not the platform's. ALWAYS shown, like Platform.
    { key: "platform_sessions", sortKey: "platform_sessions", label: "Platform sessions", kind: "count", group: "EA Matrix", total: { kind: "sum" }, width: 160 },
    { key: "sessions", label: "EA sessions", kind: "count", sortKey: "sessions", group: "EA Matrix", total: { kind: "sum" }, width: 130 },
    // ECOM TRAFFIC: the EA sessions that are NOT non-ecom (the landing-page reports' non-ecom rule, packages/ch fragments/non-ecom.ts).
    // A session belongs to exactly one entity, so the count adds up; the % is total ecom over total EA sessions, not an average of rows.
    { key: "ecom_traffic", sortKey: "ecom_traffic", label: "Ecom traffic", kind: "count", group: "EA Matrix", total: { kind: "sum" }, width: 130 },
    { key: "ecom_traffic_percentage", sortKey: "ecom_traffic_percentage", label: "Ecom traffic %", kind: "percent", group: "EA Matrix", total: { kind: "ratio", numerator: "ecom_traffic", denominator: "sessions", scale: 100 }, width: 145 },
    {
        key: "cps",
        sortKey: "cps",
        label: "CPS",
        kind: "money",
        group: "EA Matrix",
        // Total spend over total EA sessions — not the average of the per-entity CPS values,
        // which would weight a low-traffic campaign the same as a high one. Identical to
        // Source Attribution's own CPS total.
        total: { kind: "ratio", numerator: "spend", denominator: "sessions" },
    },
    // NOT SUMMED: a visitor exposed to two campaigns is a row under each, so adding up
    // the column would count that person twice. `kind: "distinct"` reads the API's own
    // DISTINCT count instead (see `reportVisitorTotalsFromRows`) and refuses to sum the
    // rows itself — see `columnTotal`.
    { key: "visitors", sortKey: "visitors", label: "Unique visitors", kind: "count", group: "EA Matrix", total: { kind: "distinct" }, width: 140 },
    { key: "unique_visitor_percentage", sortKey: "unique_visitor_percentage", label: "Unique visitor %", kind: "percent", group: "EA Matrix", noTotalReason: "No total: percentage of total unique visitors across entities.", width: 145 },
    { key: "new_visitors", sortKey: "new_visitors", label: "New visitors", kind: "count", group: "EA Matrix", total: { kind: "distinct" }, width: 140 },
    { key: "new_visitor_percentage", sortKey: "new_visitor_percentage", label: "New visitor %", kind: "percent", group: "EA Matrix", noTotalReason: "No total: both sides overlap across entities.", width: 145 },
    // The two metrics Source Attribution's EA Matrix has shown since the 2026-09 redesign and
    // the campaign reports never did — which is what "not showing complete line items/metrics
    // defined in EA Metrics Matrix" meant.
    {
        key: "single_session_orders",
        journeyModel: "single_session",
        sortKey: "single_session_orders",
        label: "Single session orders",
        kind: "count",
        group: "EA Matrix",
        // NO TOTAL, unlike Source Attribution's identical column, and the difference is the
        // point. There it covers every order and sums to the store's real single-session
        // count. Here it covers only the orders whose touch RESOLVED to a campaign — 1,201 of
        // Source Attribution's 3,375 on dev. A summed footer would disagree with Source
        // Attribution on the same window, which is the exact complaint the DDA Orders ticket
        // was; shipping a second one would repeat that mistake. Per campaign the number is
        // correct and is what the EA Matrix asks for. The total needs the unattributed line
        // to carry its complement first.
        noTotalReason: "No total: this counts only single-session orders whose touch resolved to a campaign, so it does not add up to the store's figure in Source Attribution.",
        width: 180,
    },
];

/**
 * Source Attribution's model picker, ported from `ecom_code`'s two dropdowns:
 *
 *  1. "What is your objective?" — PINS the model set: "Budget distribution across
 *     channels" shows only Data-driven; "Establish a baseline for unbiased marketing
 *     view" shows only Linear. Either way, Platform and EA Matrix stay on screen.
 *  2. "Attribution models" — a free multi-select over the same five groups, enabled
 *     only while no objective is pinned. Defaults to First click + Last click +
 *     Data-driven, matching the report's pre-redesign columns exactly.
 */
const SOURCE_ATTRIBUTION_MODEL_TOGGLE: AttributionModelToggle = {
    alwaysGroups: ["Platform", "EA Matrix", "New customer", "Assistance attribution"],
    models: [
        { key: "FC", label: "First click", groups: ["First click"] },
        { key: "LC", label: "Last click", groups: ["Last click"] },
        { key: "DDA", label: "Data-driven", groups: ["Data-driven"] },
        { key: "Triple", label: "Triple attribution", groups: ["Triple"] },
        { key: "Linear", label: "Linear attribution", groups: ["Linear"] },
    ],
    objectives: [
        {
            key: "budget",
            title: "Budget distribution across channels",
            description: "Uses data-driven attribution to split credit by each touch's modelled contribution.",
            modelKeys: ["DDA"],
        },
        {
            key: "baseline",
            title: "Establish a baseline for unbiased marketing view",
            description: "Uses linear attribution to divide conversion credit equally across every touchpoint.",
            modelKeys: ["Linear"],
        },
    ],
    defaultModelKeys: ["FC"],
};

/**
 * The Meta and Google Campaign Attribution reports' model picker, ported from
 * `ecom_code`'s redesign (owner request, 2026-09-11) — the SAME mechanism as
 * `SOURCE_ATTRIBUTION_MODEL_TOGGLE` above, with three objectives instead of two:
 *
 *  1. "Optimize the account" — pins Triple + DDA.
 *  2. "Analyse awareness" — pins First click + DDA.
 *  3. "Analyse final push towards conversion" — pins Last click + DDA.
 *
 * Either way, Platform and EA Matrix stay on screen. The free multi-select defaults to
 * First click + Last click + Data-driven, matching each report's pre-redesign columns.
 */
const CAMPAIGN_ATTRIBUTION_MODEL_TOGGLE: AttributionModelToggle = {
    alwaysGroups: ["Platform", "EA Matrix", "New customer", "Assistance attribution"],
    models: [
        { key: "FC", label: "First click", groups: ["First click"] },
        { key: "LC", label: "Last click", groups: ["Last click"] },
        { key: "DDA", label: "Data-driven", groups: ["Data-driven"] },
        { key: "Triple", label: "Triple attribution", groups: ["Triple"] },
        { key: "Linear", label: "Linear attribution", groups: ["Linear"] },
    ],
    objectives: [
        {
            key: "optimize",
            title: "Optimize the account",
            description: "Uses Triple and Data-Driven Attribution to allocate scaling budget efficiently based on conversion influence.",
            modelKeys: ["Triple", "DDA"],
        },
        {
            key: "awareness",
            title: "Analyse Awareness",
            description: "Uses First-Click and Data-Driven Attribution to analyze brand discovery and top-of-funnel reach.",
            modelKeys: ["FC", "DDA"],
        },
        {
            key: "conversion",
            title: "Analyse final push towards conversion",
            description: "Uses Last-Click and Data-Driven Attribution to evaluate direct conversion closers and bottom-of-funnel drivers.",
            modelKeys: ["LC", "DDA"],
        },
    ],
    defaultModelKeys: ["FC"],
};

export const REPORTS: readonly ReportDefinition[] = [
    {
        key: "landing-pages",
        label: "Landing Page Analysis",
        subtitle: "How each landing page performs — traffic, engagement and the orders it produced.",
        unattributedLabel: "Orders with no landing page",
        coverageKey: "order_count",
        scopeNote:
            "Counts sessions that recorded a landing page, and the orders those sessions produced. An order whose session was never tracked has no landing page and is not counted here — Source Attribution shows it under [Blank]. Sessions and visitors are per landing page, so one session visiting three pages appears three times and the column does not add up to your session count.",
        path: "/api/v2/websiteanalysis/getLandingPageAnalysis",
        columns: LANDING_PAGE_COLUMNS,
        defaultSort: "session_count",
        sortable: true,
    },
    {
        key: "landing-page-trend",
        label: "Landing Page Analysis (DOW)",
        subtitle: "The same landing pages, broken down week by week from the start of your range.",
        unattributedLabel: "Orders with no landing page",
        coverageKey: "order_count",
        scopeNote:
            "The same basis as Landing Page Analysis, split into weeks counted from the start of your date range — not calendar weeks. A session visiting three pages appears once per page, so those columns do not add up to your session count.",
        path: "/api/v2/websiteanalysis/getLandingPageTrend",
        // The week first: it is the axis this report exists to show.
        columns: [
            { key: "week_num", label: "Week", kind: "week" },
            ...withoutSort(LANDING_PAGE_COLUMNS),
        ],
        defaultSort: null,
        sortable: false,
    },
    {
        key: "source-attribution",
        label: "Source Attribution",
        subtitle: "Gain key insights at a glance with data-driven metrics",
        scopeNote:
            "Every order in the window is counted exactly once, under the source of its last click; orders we could not attribute appear as [Blank]. Sessions are counted once under their own source, so these columns do add up. The campaign reports show fewer orders than this one, because an order only appears there if its click resolved to a specific campaign.",
        path: "/api/v2/marketattribution/getSourceAttributionAnalysis",
        columns: [
            // NAME AND PLATFORM ARE FROZEN (owner request, 2026-09-11) — scrolling right
            // into FC/LC/DDA/Linear/Triple territory must never lose sight of which source
            // this is or what the platform itself spent and claims.
            { key: "ea_source", sortKey: "ea_source", label: "Source", kind: "text", brandMark: true, sticky: true, width: 200 },

            // What the ad platform itself reports: what we spent, its own reported Purchases
            // and the revenue IT claims. ALWAYS shown — see `SOURCE_ATTRIBUTION_MODELS` below.
            { key: "spend", label: "Spend", kind: "money", sortKey: "spend", group: "Platform", total: { kind: "sum" }, sticky: true, width: 110 },
            { key: "revenue", label: "Revenue", kind: "money", sortKey: "revenue", group: "Platform", total: { kind: "sum" }, sticky: true, width: 120 },
            { key: "roas", label: "ROAS", kind: "ratio", sortKey: "roas", group: "Platform", total: { kind: "ratio", numerator: "revenue", denominator: "spend" }, sticky: true, width: 90 },
            // The platform's OWN reported conversions — not derived from our order matching,
            // unlike every other Purchases column here, so it is never clickable through to a
            // journey list the way theirs are.
            { key: "platform_orders", sortKey: "platform_orders", label: "Purchases", kind: "count", group: "Platform", total: { kind: "sum" }, sticky: true, width: 100 },

            // First click: the source that STARTED the journey.
            { key: "first_click_orders", journeyModel: "fc", sortKey: "first_click_orders", label: "Purchases", kind: "count", group: "First click", total: { kind: "sum" } },
            { key: "first_click_revenue", sortKey: "first_click_revenue", label: "Revenue", kind: "money", group: "First click", total: { kind: "sum" } },
            { key: "fc_ea_roas", sortKey: "fc_ea_roas", label: "ROAS", kind: "ratio", group: "First click", total: { kind: "ratio", numerator: "first_click_revenue", denominator: "spend" } },
            { key: "first_click_new_customer_percentage", sortKey: "first_click_new_customer_percentage", label: "New customer %", kind: "percent", group: "First click", noTotalReason: "No total: both sides overlap across sources.", width: 140 },

            // Last click: the source that closed it, BEFORE cancellations and refunds — what it
            // sold, every order at its own value (the default view, 2026-10-30). Not clickable
            // through to the journey list: that list is the kept orders, so its count would differ.
            { key: "original_orders", returnsView: "original", label: "Purchases", kind: "count", sortKey: "original_orders", group: "Last click", total: { kind: "sum" } },
            { key: "original_revenue", returnsView: "original", label: "Revenue", kind: "money", sortKey: "original_revenue", group: "Last click", total: { kind: "sum" } },
            { key: "original_roas", returnsView: "original", label: "ROAS", kind: "ratio", sortKey: "original_roas", group: "Last click", total: { kind: "ratio", numerator: "original_revenue", denominator: "spend" } },
            // ...and AFTER them. `order_count` and `total_sales` ARE the last-click figures — see
            // the `attributed_orders` CTE in the report's SQL. Cancelled and Refunds say what the
            // merchant's cancellations and returns took, source by source.
            { key: "order_count", returnsView: "net", journeyModel: "lc", label: "Purchases", kind: "count", sortKey: "order_count", group: "Last click", total: { kind: "sum" } },
            { key: "total_sales", returnsView: "net", label: "Revenue", kind: "money", sortKey: "total_sales", group: "Last click", total: { kind: "sum" } },
            { key: "lc_ea_roas", returnsView: "net", sortKey: "lc_ea_roas", label: "ROAS", kind: "ratio", group: "Last click", total: { kind: "ratio", numerator: "total_sales", denominator: "spend" } },
            { key: "cancelled_orders", returnsView: "net", sortKey: "cancelled_orders", label: "Cancelled", kind: "count", group: "Last click", total: { kind: "sum" } },
            { key: "cancelled_revenue", returnsView: "net", sortKey: "cancelled_revenue", label: "Cancelled value", kind: "money", group: "Last click", total: { kind: "sum" }, width: 140 },
            { key: "refunded_orders", returnsView: "net", sortKey: "refunded_orders", label: "Refunded", kind: "count", group: "Last click", total: { kind: "sum" } },
            { key: "refund_amount", returnsView: "net", sortKey: "refund_amount", label: "Refunds", kind: "money", group: "Last click", total: { kind: "sum" } },

            /**
             * FRACTIONAL, unlike the first- and last-click Purchases beside it.
             *
             * Those two hand a whole order to ONE source, so their counts are integers. The
             * data-driven model SPLITS an order across every source that helped, so the matching
             * count is each source's SHARE: an order credited 60/40 between meta and google is
             * 0.6 of a purchase for meta and 0.4 for google. The shares sum to 1 per order, so
             * this column totals to the same number of orders as the other two — which is the
             * property that lets the three Purchases totals be compared at all.
             *
             * Counting distinct orders per source instead would be an integer and WRONG: an
             * order touching three sources would add three purchases and the column would claim
             * more orders than the store took.
             */
            { key: "dda_orders", journeyModel: "dda", sortKey: "dda_orders", label: "Purchases", kind: "count", group: "Data-driven", total: { kind: "sum" } },
            { key: "dda_revenue", sortKey: "dda_revenue", label: "Revenue", kind: "money", group: "Data-driven", total: { kind: "sum" } },
            { key: "dda_roas", sortKey: "dda_roas", label: "ROAS", kind: "ratio", group: "Data-driven", total: { kind: "ratio", numerator: "dda_revenue", denominator: "spend" } },

            // Linear — the "unbiased baseline": every touch on an order gets an EQUAL share,
            // whichever source it belongs to. Fractional for the same reason DDA's is.
            { key: "linear_orders", journeyModel: "linear", sortKey: "linear_orders", label: "Purchases", kind: "count", group: "Linear", total: { kind: "sum" } },
            { key: "linear_revenue", sortKey: "linear_revenue", label: "Revenue", kind: "money", group: "Linear", total: { kind: "sum" } },
            { key: "linear_roas", sortKey: "linear_roas", label: "ROAS", kind: "ratio", group: "Linear", total: { kind: "ratio", numerator: "linear_revenue", denominator: "spend" } },

            // Triple — every source an order touched gets the WHOLE order, full revenue, no
            // sharing. NO TOTAL: an order touching two sources is counted, in full, under both.
            { key: "triple_orders", journeyModel: "triple", sortKey: "triple_orders", label: "Purchases", kind: "count", group: "Triple", noTotalReason: "No total: an order touching two sources is counted in full under each, so adding the column would count it twice." },
            { key: "triple_revenue", sortKey: "triple_revenue", label: "Revenue", kind: "money", group: "Triple", noTotalReason: "No total: an order touching two sources is counted in full under each, so adding the column would count its revenue twice." },
            { key: "triple_roas", sortKey: "triple_roas", label: "ROAS", kind: "ratio", group: "Triple", noTotalReason: "No total: the revenue above overlaps between sources, so there is no honest ratio to sum." },

            // ASSISTANCE ATTRIBUTION (ecom_code's assisted attribution): orders this source ASSISTED — it had a touch in the order's journey that was
            // not the last click — and those orders' revenue. NO TOTAL: an order assisted by two sources is counted under each.
            { key: "assisted_orders", sortKey: "assisted_orders", label: "Orders", kind: "count", group: "Assistance attribution", noTotalReason: "No total: an order assisted by two sources is counted under each, so adding the column would count it twice.", width: 120 },
            { key: "assisted_revenue", sortKey: "assisted_revenue", label: "Revenue", kind: "money", group: "Assistance attribution", noTotalReason: "No total: an order assisted by two sources is counted under each, so adding the column would count its revenue twice.", width: 130 },

            // NEW CUSTOMER — first-click buyers new to the store in the window, and what each cost. NOT SUMMED (a buyer new under
            // two sources is one customer): `distinct` reads the API's own count. NCAC total = total spend / that distinct count.
            { key: "new_customers", sortKey: "new_customers", label: "New customers", kind: "count", group: "New customer", total: { kind: "distinct" }, width: 140 },
            { key: "ncac", sortKey: "ncac", label: "NCAC", kind: "money", group: "New customer", total: { kind: "ratio", numerator: "spend", denominator: "new_customers", totalsOnly: true }, width: 120 },

            // EA Matrix — our OWN tracking, not the platform's. ALWAYS shown, like Platform.
            { key: "platform_sessions", sortKey: "platform_sessions", label: "Platform sessions", kind: "count", group: "EA Matrix", total: { kind: "sum" } },
            { key: "sessions", label: "EA sessions", kind: "count", sortKey: "sessions", group: "EA Matrix", total: { kind: "sum" } },
            // ECOM TRAFFIC: the EA sessions that are NOT non-ecom (fragments/non-ecom.ts). A session belongs to one source, so it adds up.
            { key: "ecom_traffic", sortKey: "ecom_traffic", label: "Ecom traffic", kind: "count", group: "EA Matrix", total: { kind: "sum" }, width: 130 },
            { key: "ecom_traffic_percentage", sortKey: "ecom_traffic_percentage", label: "Ecom traffic %", kind: "percent", group: "EA Matrix", total: { kind: "ratio", numerator: "ecom_traffic", denominator: "sessions", scale: 100 }, width: 145 },
            {
                key: "cps",
                sortKey: "cps",
                label: "CPS",
                kind: "money",
                group: "EA Matrix",
                // Total spend over total EA sessions — not the average of the per-source CPS
                // values, which would weight a low-traffic source the same as a high one.
                total: { kind: "ratio", numerator: "spend", denominator: "sessions" },
            },
            // NOT SUMMED: a visitor who arrives from two sources is a row under both, so
            // adding up the column would count that person twice. `kind: "distinct"` reads
            // the API's own DISTINCT count instead (see `reportVisitorTotalsFromRows`),
            // same mechanism as the campaign reports — see `columnTotal`.
            { key: "visitors", sortKey: "visitors", label: "Unique visitors", kind: "count", group: "EA Matrix", total: { kind: "distinct" }, width: 140 },
            { key: "unique_visitor_percentage", sortKey: "unique_visitor_percentage", label: "Unique visitor %", kind: "percent", group: "EA Matrix", noTotalReason: "No total: percentage of total unique visitors across sources.", width: 145 },
            { key: "new_visitors", sortKey: "new_visitors", label: "New visitors", kind: "count", group: "EA Matrix", total: { kind: "distinct" }, width: 140 },
            { key: "new_visitor_percentage", sortKey: "new_visitor_percentage", label: "New visitor %", kind: "percent", group: "EA Matrix", noTotalReason: "No total: both sides overlap across sources.", width: 145 },
            { key: "single_session_orders", journeyModel: "single_session", sortKey: "single_session_orders", label: "Single session orders", kind: "count", group: "EA Matrix", total: { kind: "sum" }, width: 180 },

            // Columns the redesign stopped showing by default — commented out, not deleted
            // (owner instruction, 2026-09-11), the same way legacy keeps them for later:
            // {
            //     key: "conversion_rate",
            //     label: "CVR",
            //     kind: "percent",
            //     sortKey: "conversion_rate",
            //     group: "EA Matrix",
            //     total: { kind: "ratio", numerator: "order_count", denominator: "sessions", scale: 100 },
            // },
            // {
            //     key: "aov",
            //     sortKey: "aov",
            //     label: "AOV",
            //     kind: "money",
            //     group: "EA Matrix",
            //     total: { kind: "ratio", numerator: "total_sales", denominator: "order_count" },
            // },
            // {
            //     key: "cpa",
            //     sortKey: "cpa",
            //     label: "CPA",
            //     kind: "money",
            //     group: "EA Matrix",
            //     total: { kind: "ratio", numerator: "spend", denominator: "order_count" },
            // },
            // NO TOTAL: any-touch orders overlap across sources — see ColumnTotal.
            // { key: "total_orders", label: "Orders touched", kind: "count", group: "Any touch", noTotalReason: "No total: one order can touch several sources and is counted under each, so the column adds up to more orders than exist." },
        ],
        defaultSort: "dda_revenue",
        sortable: true,
        totalKey: "total_source_count",
        attributionModelToggle: SOURCE_ATTRIBUTION_MODEL_TOGGLE,
        periodBreakdown: { path: "/api/v2/marketattribution/getSourceAttributionBreakdown", entityKey: "ea_source", labelKey: "ea_source" },
        returnsView: true,
    },
    {
        key: "meta-campaign-attribution",
        label: "Meta Campaign Attribution",
        subtitle: "Meta campaigns, compared across platform, first-click, last-click and data-driven models.",
        unattributedLabel: "Not matched to a campaign",
        coverageKey: "lc_orders",
        scopeNote:
            "Meta campaigns only. An order appears in a campaign row only if its click resolved to a campaign; the rest are gathered in the “Not matched to a campaign” line, so the campaign rows plus that line equal Source Attribution’s meta figure. The Total row sums the campaign rows alone — adding revenue that has no matching spend would inflate ROAS. Spend and impressions come from Meta; order counts come from our own tracking, so they will not match Meta’s reported conversions either. Clicks are Meta’s “Link clicks” — the clicks that opened your store. Meta’s “Clicks (all)” is a larger number because it also counts likes, comments, shares and profile visits, which never reach the site.",
        path: "/api/v2/marketattribution/metacampaign",
        columns: CAMPAIGN_COLUMNS,
        defaultSort: "dda_revenue",
        sortable: true,
        statusFilter: true,
        metaHierarchy: true,
        attributionModelToggle: CAMPAIGN_ATTRIBUTION_MODEL_TOGGLE,
        periodBreakdown: { path: "/api/v2/marketattribution/metacampaign/breakdown", entityKey: "campaign_id", labelKey: "campaign_name" },
    },
    {
        key: "google-campaign-attribution",
        label: "Google Campaign Attribution",
        subtitle: "Google campaigns, compared across platform, first-click, last-click and data-driven models.",
        unattributedLabel: "Not matched to a campaign",
        coverageKey: "lc_orders",
        scopeNote:
            "Google campaigns only. An order appears in a campaign row only if its click resolved to a campaign; the rest are gathered in the “Not matched to a campaign” line, so the campaign rows plus that line equal Source Attribution’s google figure. The Total row sums the campaign rows alone — adding revenue that has no matching spend would inflate ROAS. Spend and impressions come from Google; order counts come from our own tracking, so they will not match Google’s reported conversions either. Google’s clicks are already click-throughs to your store, so this column means the same thing here as on the Meta report.",
        path: "/api/v2/marketattribution/googlecampaign",
        columns: CAMPAIGN_COLUMNS,
        defaultSort: "dda_revenue",
        sortable: true,
        statusFilter: true,
        googleHierarchy: true,
        attributionModelToggle: CAMPAIGN_ATTRIBUTION_MODEL_TOGGLE,
        periodBreakdown: { path: "/api/v2/marketattribution/googlecampaign/breakdown", entityKey: "campaign_id", labelKey: "campaign_name" },
    },
];

export function findReport(key: string): ReportDefinition | undefined {
    return REPORTS.find((report) => report.key === key);
}

export type ReportRow = Readonly<Record<string, string | number | null>>;

/**
 * The orders a campaign report CANNOT show: this platform's orders whose click never
 * resolved to a campaign. Present only on the campaign reports; null when there are none.
 *
 * Sent alongside the rows rather than as one of them, so it cannot be paged away — see
 * `apps/api/src/modules/reports/reports.service.ts`.
 */
export type Unattributed = Readonly<Record<string, string | number>>;

/**
 * How much of the window this report can actually account for.
 *
 * THE NUMBER THAT WOULD HAVE ANSWERED THE 2026-09-03 QUESTION IMMEDIATELY. Source
 * Attribution showed 5,652 orders for a range where Landing Page Analysis showed 2,961,
 * and the reports looked broken. They were not: v3's tracker went live for that store on
 * 30-31 August, so orders before then have no page views to land on — 100% missing on
 * 28-29 Aug, about 5% from 31 Aug onward. A coverage line says that in one sentence
 * instead of leaving a 2,691-order hole for someone to discover by comparing two pages.
 */
export function coverage(
    rows: readonly ReportRow[],
    unattributed: Unattributed | null | undefined,
    countKey: string,
): { readonly tracked: number; readonly total: number; readonly percent: number } | null {
    if (unattributed === null || unattributed === undefined) return null;
    const total = Number(unattributed["orders_in_window"] ?? 0);
    if (!Number.isFinite(total) || total === 0) return null;

    const missing = Number(unattributed[countKey] ?? 0);
    const tracked = Math.max(0, total - missing);
    void rows;
    return { tracked, total, percent: Math.round((tracked / total) * 1000) / 10 };
}

/** Which keys in an `Unattributed` record are counts, for deciding whether to show it. */
// `sessions` too (2026-10-19): the campaign lines carry their unplaced EA sessions, and a line with
// sessions but no orders (a quiet day, a drilled ad set) is still part of rows + line = the total.
const UNATTRIBUTED_COUNT_KEYS = ["lc_orders", "fc_orders", "order_count", "sessions"] as const;

/**
 * Is there anything to reconcile?
 *
 * The API returns zeros rather than null when everything matched, and a permanent
 * "…— 0" line is noise on a table that is already eighteen columns wide. The row earns
 * its space only when it changes the arithmetic.
 */
export function hasUnattributed(row: Unattributed | null | undefined): row is Unattributed {
    if (row === null || row === undefined) return false;
    return UNATTRIBUTED_COUNT_KEYS.some((key) => Number(row[key] ?? 0) > 0);
}

export interface ReportPayload {
    readonly rows: readonly ReportRow[];
    /** Echoed by the API so the screen can say WHICH zone produced these day boundaries. */
    readonly timezone: string;
    readonly unattributed?: Unattributed | null;
    /**
     * Window-wide sums, computed by the API over every row — not just the page on screen.
     * Absent only if an older API is answering; the footer falls back to a page sum and
     * says so.
     */
    readonly totals?: Readonly<Record<string, number>> | null;
    /**
     * Source Attribution only: the store's Total sales as Shopify counts them — each cancellation
     * and refund on the day it happened (packages/ch store-total-sales.ts). null when it failed.
     */
    readonly storeTotalSales?: StoreTotalSales | null;
    /** Landing Page Analysis only: when its snapshot was built (ISO, UTC); null or absent = live. */
    readonly dataAsOf?: string | null;
}

export interface StoreTotalSales {
    readonly total_sales: number;
    readonly orders_value: number;
    readonly cancellations: number;
    readonly refunds: number;
    /** Cancelled in these dates but NOT subtracted: not voided, so Shopify keeps them as sales (2026-10-28). */
    readonly cancellations_kept: number;
    /** Counts, so each amount says how many orders or refunds it covers. 64-bit counts arrive as strings. */
    readonly orders_placed: string | number;
    readonly orders_cancelled: string | number;
    readonly orders_cancelled_kept: string | number;
    readonly refund_count: string | number;
}

export interface ReportQuery {
    readonly from: string;
    readonly to: string;
    readonly limit: number;
    readonly offset: number;
    readonly sortBy?: string;
    readonly direction?: "asc" | "desc";
    readonly status?: CampaignStatus;
    /** Google-only: restrict to one Ads channel type, or the synthetic "UNATTRIBUTED" bucket. */
    readonly campaignType?: GoogleCampaignType;
    /**
     * Drill level. Its valid values depend on which platform's report this is —
     * GoogleCampaignHierarchy on the Google report, MetaCampaignHierarchy on Meta's.
     * Left as `string` here rather than a union of the two so this one query shape can
     * serve both; each report's own allow-list (see ALLOWED_GOOGLE_HIERARCHY_BY_CAMPAIGN_TYPE,
     * META_CAMPAIGN_HIERARCHIES) is what actually constrains it.
     */
    readonly hierarchy?: string;

    /**
     * Meta-only: scope to the parent picked by a drill-down click. `campaignId` narrows
     * an "adset" or "ad" request to one campaign's children; `adsetId` further narrows an
     * "ad" request to one adset's children. Unset means "no parent" — the unscoped view a
     * manual Level-dropdown pick already produces today. Not sent for Google: its drill
     * uses the existing campaignType/hierarchy controls only, so this never alters that
     * report's contract.
     */
    readonly campaignId?: string;
    readonly adsetId?: string;
    /** Google-only counterpart of `adsetId`. */
    readonly adgroupId?: string;
    /**
     * The table's search box, matched in SQL over EVERY row of the report and paged after the
     * filter (2026-10-16) — it used to filter only the page already loaded. Blank = omitted.
     */
    readonly search?: string;
}

export async function fetchReport(
    client: AxiosInstance,
    report: ReportDefinition,
    query: ReportQuery,
): Promise<ReportPayload> {
    /**
     * `sortBy`/`direction` are omitted entirely for a report whose schema has no such
     * field. zod strips unknown keys silently rather than rejecting them, so sending them
     * would not error — it would just be a control that appears to work and changes
     * nothing, which is the harder bug to notice.
     */
    const params: Record<string, string | number> = {
        from: query.from,
        to: query.to,
        limit: query.limit,
        offset: query.offset,
    };
    if (report.sortable && query.sortBy !== undefined) {
        params["sortBy"] = query.sortBy;
        params["direction"] = query.direction ?? "desc";
    }
    // Same reasoning as `sortBy` above, and `all` is omitted too: it is the server's own
    // default, so sending it adds a parameter that changes nothing.
    if (report.statusFilter === true && query.status !== undefined && query.status !== "all") {
        params["status"] = query.status;
    }

    // campaignType is Google-only — Meta's schema has no such field.
    if (report.googleHierarchy === true && query.campaignType !== undefined) {
        params["campaignType"] = query.campaignType;
    }
    // hierarchy is shared shape, sent for either platform's report — "campaign" is both
    // schemas' own default, so it's omitted rather than sent redundantly.
    if ((report.googleHierarchy === true || report.metaHierarchy === true) && query.hierarchy !== undefined && query.hierarchy !== "campaign") {
        params["hierarchy"] = query.hierarchy;
    }

    // is still driven purely by the Type + Level controls, never by a clicked parent id.
    if (report.metaHierarchy === true) {
        if (query.campaignId !== undefined) params["campaignId"] = query.campaignId;
        if (query.adsetId !== undefined) params["adsetId"] = query.adsetId;
    }
    // Google's own parent-scope params — same idea, different field names, since its
    // schema validates adgroupId (not adsetId) against an "ad"-level request.
    if (report.googleHierarchy === true) {
        if (query.campaignId !== undefined) params["campaignId"] = query.campaignId;
        if (query.adgroupId !== undefined) params["adgroupId"] = query.adgroupId;
    }

    const search = query.search?.trim() ?? "";
    if (search !== "") params["search"] = search;

    const response = await client.get<{ success: boolean; message: string; data: ReportPayload }>(report.path, {
        params,
    });
    return response.data.data;
}

/** One period of one entity in the Day / Week view: the period's label and dates, then the entity's figures for it. */
export interface PeriodRow {
    readonly period_num: number;
    readonly period_label: string;
    readonly period_start: string;
    readonly period_end: string;
    readonly display_label: string;
    readonly [key: string]: string | number | null | undefined;
}

export interface PeriodBreakdownPayload {
    readonly breakdown: "day" | "week";
    readonly totalCount: number;
    readonly hasMore: boolean;
    readonly offset: number;
    readonly limit: number;
    readonly breakdowns: Readonly<Record<string, readonly PeriodRow[]>>;
}

/** Periods fetched per request — the landing-page trend's chunk size, so both screens load a block the same way. */
export const PERIOD_CHUNK_SIZE = 15;

/**
 * The Day / Week figures of the entities on screen, `PERIOD_CHUNK_SIZE` periods at a time.
 *
 * The filters are the report's own (drill level, parent campaign / ad set, campaign type) — they decide WHICH entities exist, and
 * the ids name the ones on screen. Sort, status and search are not sent: the ids already are the answer to them.
 */
export async function fetchPeriodBreakdown(
    client: AxiosInstance,
    report: ReportDefinition,
    query: {
        readonly from: string;
        readonly to: string;
        readonly breakdown: "day" | "week";
        readonly ids: readonly string[];
        readonly periodOffset: number;
        readonly periodLimit?: number;
        readonly hierarchy?: string;
        readonly campaignType?: GoogleCampaignType;
        readonly campaignId?: string;
        readonly adsetId?: string;
        readonly adgroupId?: string;
    },
): Promise<PeriodBreakdownPayload> {
    const target = report.periodBreakdown;
    if (target === undefined) throw new Error(`${report.key} has no day / week view.`);
    const params: Record<string, string | number> = {
        from: query.from,
        to: query.to,
        breakdown: query.breakdown,
        ids: query.ids.join(","),
        periodOffset: query.periodOffset,
        periodLimit: query.periodLimit ?? PERIOD_CHUNK_SIZE,
    };
    if ((report.googleHierarchy === true || report.metaHierarchy === true) && query.hierarchy !== undefined && query.hierarchy !== "campaign") {
        params["hierarchy"] = query.hierarchy;
    }
    if (report.googleHierarchy === true && query.campaignType !== undefined) params["campaignType"] = query.campaignType;
    if (report.metaHierarchy === true || report.googleHierarchy === true) {
        if (query.campaignId !== undefined) params["campaignId"] = query.campaignId;
    }
    if (report.metaHierarchy === true && query.adsetId !== undefined) params["adsetId"] = query.adsetId;
    if (report.googleHierarchy === true && query.adgroupId !== undefined) params["adgroupId"] = query.adgroupId;

    const response = await client.get<{ success: boolean; message: string; data: PeriodBreakdownPayload }>(target.path, { params });
    return response.data.data;
}

/**
 * The unmatched line's breakdown, fetched only when the drawer opens.
 *
 * The path is the report's own plus `UNMATCHED_REASONS_PATH` — the SAME concatenation
 * `reports.routes.ts` registers, from the same shared constant, so the two cannot drift
 * into a 404 that appears only when a merchant clicks.
 *
 * Only the two campaign reports have this endpoint. `supportsUnmatchedReasons` below is
 * what decides whether the row is clickable, so this is never called for a report that
 * would 404.
 */
export async function fetchUnmatchedReasons(
    client: AxiosInstance,
    report: ReportDefinition,
    query: { from: string; to: string },
): Promise<readonly UnmatchedReasonRow[]> {
    const response = await client.get<{ success: boolean; message: string; data: { rows: UnmatchedReasonRow[] } }>(
        `${report.path}${UNMATCHED_REASONS_PATH}`,
        { params: { from: query.from, to: query.to } },
    );
    return response.data.data.rows;
}

/**
 * Whether this report's unmatched line can be broken down.
 *
 * CAMPAIGN LEVEL ONLY, and the hierarchy check is the load-bearing half: one level down
 * the line means "in this campaign, but no ad set", and the reasons for a missing AD SET
 * id are a different list from the reasons for a missing CAMPAIGN id. Offering the
 * breakdown there would answer a question the merchant did not ask, confidently.
 */
export function supportsUnmatchedReasons(
    report: ReportDefinition,
    hierarchy: string | undefined,
): boolean {
    const isCampaignReport =
        report.key === "meta-campaign-attribution" || report.key === "google-campaign-attribution";
    return isCampaignReport && (hierarchy === undefined || hierarchy === "campaign");
}

/** What clicking a row at this Meta level drills into. null = "ad" is terminal. */
export const META_HIERARCHY_CHILD: Record<MetaCampaignHierarchy, MetaCampaignHierarchy | null> = {
    campaign: "adset",
    adset: "ad",
    ad: null,
};

export function cellText(kind: CellKind, value: string | number | null | undefined, key?: string): string {
    switch (kind) {
        case "count":
            return formatCount(value, key);
        case "money":
            return formatMoney(value, key);
        case "percent":
            return formatPercent(value);
        case "ratio":
            return formatRatio(value);
        case "week":
            return formatWeek(value);
        case "text":
            return formatText(value);
        case "status":
            // Title case for display; the wire value stays lowercase, which is what the filter
            // and the badge colour both match on.
            return value === "active" ? "Active" : value === "inactive" ? "Inactive" : formatText(value);
    }
}

/** The header bands, in column order, with how many columns each spans. */
export interface ColumnBand {
    readonly label: string | null;
    readonly span: number;
}

export function columnBands(columns: readonly ReportColumn[]): readonly ColumnBand[] {
    const bands: ColumnBand[] = [];
    for (const column of columns) {
        const label = column.group ?? null;
        const last = bands[bands.length - 1];
        // Adjacent columns in the same group share one band. A group that appears twice in
        // non-adjacent positions gets two bands, which is honest: the header must sit above
        // the columns it actually labels.
        if (last !== undefined && last.label === label) bands[bands.length - 1] = { label, span: last.span + 1 };
        else bands.push({ label, span: 1 });
    }
    return bands;
}

export function hasGroups(columns: readonly ReportColumn[]): boolean {
    return columns.some((column) => column.group !== undefined);
}

/**
 * The footer figure for one column, over the rows currently on screen.
 *
 * Returns null when the column has no defensible total — the footer shows an em dash.
 * See `ColumnTotal` for why several columns deliberately have none.
 */
export function columnTotal(column: ReportColumn, rows: readonly ReportRow[]): number | null {
    if (column.total === undefined || rows.length === 0) return null;

    if (column.total.kind === "sum") return sum(rows, column.key);

    /**
     * NEVER SUM — see `ColumnTotal`. `rows` here is either the API's `totals` object
     * wrapped as a single row, or (no `totals` sent) the real per-source/per-entity rows
     * on screen. Only the first shape is trustworthy for a distinct count; the second is
     * exactly the shape that double-counts a visitor reachable from two rows, so it is
     * refused rather than summed.
     */
    // A totals object WITHOUT the figure (the API omits it under a search — the report-wide
    // distinct count is the store's, not the matches') is "no total", not zero.
    if (column.total.kind === "distinct") {
        return rows.length === 1 && rows[0]?.[column.key] !== undefined ? sum(rows, column.key) : null;
    }

    const denominator = sum(rows, column.total.denominator);
    if (denominator === 0) return null;
    return (sum(rows, column.total.numerator) / denominator) * (column.total.scale ?? 1);
}

function sum(rows: readonly ReportRow[], key: string): number {
    let total = 0;
    for (const row of rows) {
        const value = row[key];
        const parsed = typeof value === "number" ? value : Number(value ?? 0);
        if (Number.isFinite(parsed)) total += parsed;
    }
    return total;
}